import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ApiEndpointInfo {
  url: string | null;
  enabled: boolean;
  lastUpdated?: string;
  endpointToken?: string | null;
  endpointTokenStatus?: string;
  endpointTokenCreatedAt?: string | null;
}

interface ApiEndpointResponse {
  success: boolean;
  message?: string;
  error?: string;
  token?: string;
}

// Hook to get API endpoint information
export const useApiEndpoint = (organizationId: string) => {
  return useQuery({
    queryKey: ['api-endpoint', organizationId],
    queryFn: async (): Promise<ApiEndpointInfo> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('api_endpoint_url, api_endpoint_enabled, api_endpoint_updated_at, api_endpoint_token_encrypted, api_endpoint_token_status, api_endpoint_token_created_at')
        .eq('id', organizationId)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        url: data.api_endpoint_url,
        enabled: data.api_endpoint_enabled || false,
        lastUpdated: data.api_endpoint_updated_at,
        endpointToken: data.api_endpoint_token_encrypted ? atob(data.api_endpoint_token_encrypted) : null,
        endpointTokenStatus: data.api_endpoint_token_status || 'inactive',
        endpointTokenCreatedAt: data.api_endpoint_token_created_at,
      };
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Hook to update API endpoint
export const useUpdateApiEndpoint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      organizationId, 
      endpointAddress, 
      enableEndpoint 
    }: { 
      organizationId: string;
      endpointAddress: string;
      enableEndpoint: boolean;
    }): Promise<ApiEndpointResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('manage-api-endpoint', {
        body: {
          organizationId,
          endpointAddress,
          enableEndpoint: enableEndpoint ? 1 : 0,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['api-endpoint', organizationId] });
      toast.success('API endpoint updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update API endpoint: ${error.message}`);
    },
  });
};

// Hook to manage API endpoint token (generate/revoke)
export const useManageApiEndpointToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      organizationId, 
      action 
    }: { 
      organizationId: string;
      action: 'generate' | 'revoke';
    }): Promise<ApiEndpointResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await supabase.functions.invoke('manage-api-endpoint', {
        body: {
          organizationId,
          tokenAction: action,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (data, { organizationId, action }) => {
      queryClient.invalidateQueries({ queryKey: ['api-endpoint', organizationId] });
      if (action === 'generate') {
        toast.success('Endpoint token generated successfully');
      } else {
        toast.success('Endpoint token revoked successfully');
      }
    },
    onError: (error) => {
      toast.error(`Failed to manage endpoint token: ${error.message}`);
    },
  });
};
